<template>
  <div class="emh-filters-content">
    <slot name="head"></slot>
    <ui-accordion-item
      v-for="category in filterCategories"
      :key="category.code"
      :open="category.code === openAccordion"
      data-test-id="emh-filters-content__accordion-item"
      @toggle="(e) => onToggleAccordion(category.code, e)"
    >
      <template #head>
        <emh-side-filters-category-head :category="category"></emh-side-filters-category-head>
      </template>
      <template #content>
        <component
          :is="getCategoryComponentName(category.code)"
          :is-loading="isLoading"
          :filter-category="category"
          data-test-id="emh-filters-content__category-filters-content"
          @change="onChangeFilter"
          @clear-category="onClearCategory"
        />
      </template>
    </ui-accordion-item>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, provide, ref, toRefs } from 'vue';
import { IProductFilterGroup } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';
import EmhSideFiltersCampaignCategory from '../sideFiltersCategories/sideFiltersCampaignCategory/SideFiltersCampaignCategory.vue';
import EmhSideFiltersCategories from '../sideFiltersCategories/SideFiltersCategories.vue';
import EmhSideFiltersCategoryContentTemplate from '../sideFiltersCategories/sideFiltersCategoryContentTemplate/SideFiltersCategoryContentTemplate.vue';
import EmhSideFiltersEquipmentCategory from '../sideFiltersCategories/sideFiltersEquipmentCategory/SideFiltersEquipmentCategory.vue';
import EmhSideFiltersPriceCategory from '../sideFiltersCategories/sideFiltersPriceCategory/SideFiltersPriceCategory.vue';
import EmhSideFiltersVehicleCategory from '../sideFiltersCategories/sideFiltersVehicleCategory/SideFiltersVehicleCategory.vue';
import EmhSideFiltersColorCategory from '../sideFiltersCategories/sideFiltersColorCategory/SideFiltersColorCategory.vue';
import EmhSideFiltersUpholsteryCategory from '../sideFiltersCategories/sideFiltersUpholsteryCategory/SideFiltersUpholsteryCategory.vue';
import EmhSideFiltersEngineCategory from '../sideFiltersCategories/sideFiltersEngineCategory/SideFiltersEngineCategory.vue';
import EmhSideFiltersLocationsCategory from '../sideFiltersCategories/sideFiltersLocationsCategory/SideFiltersLocationsCategory.vue';
import EmhSideFiltersTransportUsageCategory from '../sideFiltersCategories/sideFiltersTransportUsageCategory/SideFiltersTransportUsageCategory.vue';
import EmhSelectedFiltersCounter from '../selectedFiltersCounter/SelectedFiltersCounter.vue';
import TestingGenericWrapper from '@dh-io-emh/shared/src/components/testingGenericWrapper/TestingGenericWrapper.vue';
import UiAccordionItem from '@dh-io-emh/ui/src/components/accordion/AccordionItem.vue';
import EmhSideFiltersCategoryHead from '../sideFiltersCategories/sideFiltersCategoryHead/sideFiltersCategoryHead.vue';
import { useSideFiltersContent } from './side-filters-content.composable';
import EmhSideFiltersStockTypeCategory from '../sideFiltersCategories/sideFiltersStockTypeCategory/SideFiltersStockTypeCategory.vue';

export default defineComponent({
  name: 'EmhSideFiltersAccordionContent',
  components: {
    EmhSideFiltersTransportUsageCategory,
    EmhSideFiltersLocationsCategory,
    EmhSideFiltersCategoryContentTemplate,
    EmhSideFiltersCampaignCategory,
    EmhSideFiltersCategories,
    EmhSideFiltersColorCategory,
    EmhSideFiltersEquipmentCategory,
    EmhSideFiltersEngineCategory,
    EmhSideFiltersPriceCategory,
    EmhSideFiltersVehicleCategory,
    EmhSideFiltersUpholsteryCategory,
    EmhSelectedFiltersCounter,
    TestingGenericWrapper,
    UiAccordionItem,
    EmhSideFiltersCategoryHead,
    EmhSideFiltersStockTypeCategory,
  },

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    filterCategories: {
      type: Array as PropType<IProductFilterGroup[]>,
      default: () => [],
    },
    selectedFilters: {
      type: Array as PropType<IProductFilterType[]>,
      default: () => [],
    },
  },

  emits: ['change-category', 'change-filter', 'clear-all-filters', 'clear-category', 'select-subcategory'],

  setup(props, { emit }) {
    // TODO: find better solution once Accordion AB Testing is done
    // this will hide the divider lines in SideFiltersCategoryFilterTemplate
    provide('hideDivider', true);

    const { filterCategories, selectedFilters } = toRefs(props);

    const { onChangeFilter, onClearCategory, getCategoryComponentName, filteredSideFilters } = useSideFiltersContent(
      filterCategories,
      selectedFilters,
      emit,
    );

    const openAccordion = ref<string | undefined>(undefined);

    const onToggleAccordion = (code, isOpen) => {
      openAccordion.value = isOpen ? code : undefined;
    };

    return {
      onChangeFilter,
      onClearCategory,
      getCategoryComponentName,
      onToggleAccordion,
      openAccordion,
      filteredSideFilters,
    };
  },
});
</script>

<style lang="scss" scoped>
.emh-filters-content {
  // add additional spacing to enforce scrolling when 2 buttons are shown in modal
  margin-bottom: 152px;
  color: var(--wb-grey-20);
}
</style>
